import * as React from "react";
import styled from "styled-components";

import Tooltip from "@ingka/tooltip";

import { getImage } from "../utils/markets";
import { useGetCountryName } from "../hooks/useGetCountryName";

interface FlagsRowPros {
	markets: string[];
	size: size;
	tooltipBoundaryContainerID?: string;
	truncateAfter?: number;
}

interface MarketImageProps {
	height: size;
	width: size;
}

type size = 25 | 50 | 75 | 100 | 125 | 150 | 200 | 250 | 300 | 400 | 550 | 750 | 1000 | 1350;

const MarketImage = styled.img<MarketImageProps>`
	height: ${({ height }) => (height ? height / 100 + "rem" : "0")};
	width: ${({ width }) => (width ? width / 100 + "rem" : "0")};
	margin-right: 0.5rem;
	margin-top: 0.5rem;
`;

const MarketsWrapper = styled.span`
	display: flex;
	align-items: center;
	overflow: auto;
	flex-wrap: wrap;
	list-style: none;
	gap: 2px;
	padding: 0;
`;

const StyledParagraph = styled.p`
	margin: 0;
`;

const FlagsRow: React.FC<FlagsRowPros> = ({ markets, size, tooltipBoundaryContainerID, truncateAfter }) => {
	const marketsData = [];
	const { getCountryName } = useGetCountryName();
	for (let i = 0; i < markets.length; i++) {
		if (truncateAfter ? i < truncateAfter : i < 999) {
			marketsData.push(
				<span key={markets[i]}>
					<MarketImage
						key={`img-${markets[i]}`}
						src={getImage(markets[i]) || getImage("xx")}
						width={size}
						height={size}
						alt={getCountryName(markets[i])}
					/>
				</span>
			);
		} else {
			marketsData.push(
				<li key="no-img" aria-label={`and ${markets.length - (truncateAfter || 0)} more markets`}>
					<strong>...</strong>
				</li>
			);
			break;
		}
	}

	const marketsTooltipText = (_markets: string[]) =>
		_markets
			.map((market) => `${getCountryName(market)} (${market})`)
			.toString()
			.replaceAll(",", ", ")
			.replace(/,(?=[^,]+$)/, " and ");

	return marketsData.length ? (
		<Tooltip
			interactiveCustomTrigger
			boundaryContainerID={tooltipBoundaryContainerID}
			position="top"
			tooltipText={marketsTooltipText(markets)}
		>
			<MarketsWrapper aria-label={`Markets list, ${markets.length} countries`}>
				{marketsData.map((market) => market)}
			</MarketsWrapper>
		</Tooltip>
	) : (
		<StyledParagraph aria-label="No markets">-</StyledParagraph>
	);
};

export default FlagsRow;
